import type { Polygon } from 'geojson';

/**
 * Incident workspace seeded for the Equinor demo. Incidents have no demo_key,
 * so this exact name is the idempotency handle.
 */
export const EQUINOR_GRANE_INCIDENT_NAME =
  'Grane Facility uncontrolled release';

export const EQUINOR_GRANE_INCIDENT_TYPE = 'Oil Spill';

export const EQUINOR_DIVISIONS_LAYER_NAME = 'ICS Divisions';

export const EQUINOR_DIVISIONS_LAYER_DESCRIPTION =
  'Operational divisions across Utsira for the Grane Facility uncontrolled release.';

/** Bounds of the island the four divisions tile. */
export const UTSIRA_ISLAND_BOX = {
  minLon: 4.855,
  maxLon: 4.905,
  minLat: 59.293,
  maxLat: 59.327,
};

/** Shared edges, so the four zones stay adjacent rather than overlapping. */
const WEST_EDGE = 4.873;
const HARBOUR_EDGE = 59.314;
const TUVEVAGEN_EDGE = 59.3045;

export type EquinorDivision = {
  name: string;
  description: string;
  polygonColor: string;
  polygonOpacity: number;
  geometry: Polygon;
};

function box(
  minLon: number,
  minLat: number,
  maxLon: number,
  maxLat: number
): Polygon {
  return {
    type: 'Polygon',
    coordinates: [
      [
        [minLon, minLat],
        [maxLon, minLat],
        [maxLon, maxLat],
        [minLon, maxLat],
        [minLon, minLat],
      ],
    ],
  };
}

/**
 * Four geographic divisions covering Utsira itself. The response is ashore, so
 * each division is an area of the island rather than a slice of the offshore
 * plume. Together they tile `UTSIRA_ISLAND_BOX` exactly.
 */
export const EQUINOR_DIVISIONS: EquinorDivision[] = [
  {
    name: 'Division A',
    description:
      'West coast. Heaviest shoreline oiling, exposed to the incoming slick. Assessment and shoreline cleanup.',
    polygonColor: '#b91c1c',
    polygonOpacity: 0.25,
    geometry: box(
      UTSIRA_ISLAND_BOX.minLon,
      UTSIRA_ISLAND_BOX.minLat,
      WEST_EDGE,
      UTSIRA_ISLAND_BOX.maxLat
    ),
  },
  {
    name: 'Division B',
    description:
      'North coast. Oiled shoreline north of the harbour, working back toward the west coast.',
    polygonColor: '#c2410c',
    polygonOpacity: 0.25,
    geometry: box(
      WEST_EDGE,
      HARBOUR_EDGE,
      UTSIRA_ISLAND_BOX.maxLon,
      UTSIRA_ISLAND_BOX.maxLat
    ),
  },
  {
    name: 'Division C',
    description:
      'Main harbour and Sjoarskogen. Harbour is oiled and reserved for recovery, not for resource movement.',
    polygonColor: '#a16207',
    polygonOpacity: 0.25,
    geometry: box(
      WEST_EDGE,
      TUVEVAGEN_EDGE,
      UTSIRA_ISLAND_BOX.maxLon,
      HARBOUR_EDGE
    ),
  },
  {
    name: 'Division D',
    description:
      'Tuvevagen and the south of the island. Clean landing site for response resources staged from Stavanger.',
    polygonColor: '#1d4ed8',
    polygonOpacity: 0.25,
    geometry: box(
      WEST_EDGE,
      UTSIRA_ISLAND_BOX.minLat,
      UTSIRA_ISLAND_BOX.maxLon,
      TUVEVAGEN_EDGE
    ),
  },
];
